import { useState } from 'react';
import { useEditMode } from '@/context/EditModeContext';
import { useSiteData } from '@/context/SiteDataContext';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Link as LinkIcon } from 'lucide-react';

/**
 * A tiny "🔗" pill that appears on hover in edit mode and lets an admin change
 * the URL of the wrapped link/button. Store the destination on `settings` or
 * `page:{slug}` under a chosen field.
 */
export default function EditLink({ field, target = 'settings', currentHref, children, className = '' }) {
  const { editMode } = useEditMode();
  const { refresh, loadPageContent } = useSiteData();
  const [busy, setBusy] = useState(false);

  const prompt_ = async () => {
    const next = window.prompt('Enter destination URL or path (e.g. /giving, https://givelify.com/...):', currentHref || '');
    if (next == null) return;
    setBusy(true);
    try {
      if (target === 'settings') {
        await api.patch('/site/settings', { [field]: next });
        refresh(['settings']);
      } else if (target?.startsWith('page:')) {
        const slug = target.slice(5);
        await api.patch(`/site/page-content/${slug}`, { [field]: next });
        loadPageContent(slug);
      }
      toast.success('Link updated');
    } catch { toast.error('Could not update link'); }
    finally { setBusy(false); }
  };

  if (!editMode) return children;

  return (
    <span className={`relative inline-flex align-middle editable-link ${className}`}>
      {children}
      <button
        type="button"
        disabled={busy}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); prompt_(); }}
        title={`Edit destination URL${currentHref ? ` (currently: ${currentHref})` : ''}`}
        data-testid={`edit-link-${field}`}
        className="absolute -top-2 -right-2 z-30 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-gold text-royal-dark text-[10px] font-bold shadow-lg hover:bg-royal hover:text-gold transition"
      >
        <LinkIcon className="w-2.5 h-2.5" /> URL
      </button>
    </span>
  );
}
